/* eslint-env jest */
import PromoBanner from './index';

// PromoBanner is a pure function component (no hooks), so we invoke it directly and
// read the text out of the returned element tree.
const textOf = (node, acc = []) => {
  if (node == null || node === false) return acc;
  if (typeof node === 'string' || typeof node === 'number') {
    acc.push(String(node));
    return acc;
  }
  if (Array.isArray(node)) {
    node.forEach((n) => textOf(n, acc));
    return acc;
  }
  if (node.props && node.props.children != null) textOf(node.props.children, acc);
  return acc;
};
const render = (offer) => textOf(PromoBanner({ offer, className: '' })).join(' ');

// expiryMoment parses "YYYY-MM-DD" as LOCAL midnight, so the fixture has to be a
// local calendar date too. Building it from toISOString() (as these tests used to)
// is UTC and lands a day late for any afternoon run west of Greenwich -- which
// silently shifts the phase under test.
const localDateIn = (days) => {
  const d = new Date();
  d.setDate(d.getDate() + days);
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};

describe('PromoBanner', () => {
  it('renders nothing without an offer', () => {
    expect(PromoBanner({ offer: null, className: '' })).toBeNull();
  });

  it('shows the admin promoHeadline when set', () => {
    expect(render({
      promoHeadline: 'Save big today', couponType: 'PERCENTAGE', value: 20, code: 'X',
    })).toContain('Save big today');
  });

  it('falls back to offerLine when promoHeadline is absent (offer + code stay visible)', () => {
    const text = render({ couponType: 'PERCENTAGE_WITH_FREE_SHIPPING', value: 20, code: 'FALL20' });
    expect(text).toContain('20% off');
    expect(text).toContain('free shipping');
    expect(text).toContain('FALL20');
  });

  // UPDATED for #4667. This example previously asserted that a flat-rate coupon rendered
  // nothing, on the premise that a flat coupon had "nothing derivable". #4667 taught
  // offerPricing to derive a credit line, so a flat coupon IS derivable now and the banner
  // correctly shows it -- which is the whole point of that change: the cards show the credit
  // but not the CODE, so without this banner a flat coupon would have its code nowhere.
  // The component's own contract comment states the rule: "Renders nothing only when there's
  // no offer AND nothing derivable." The test was left behind; the code is right. (#4673)
  it('derives a credit line for a flat-rate coupon with no headline', () => {
    const text = render({ couponType: 'FLAT_RATE', value: 5, code: 'X' });

    expect(text).toContain('$5 credit');
    expect(text).toContain('X');
  });

  // The server sends expiresOn only on the for_landing_page path. Everywhere
  // else the banner must look exactly as it did before this change.
  it('falls back to the static subhead when no date was sent', () => {
    const text = render({ promoHeadline: '20% OFF', promoSubhead: 'Applied at checkout' });

    expect(text).toContain('20% OFF');
    expect(text).toContain('Applied at checkout');
  });

  it('shows the real date instead of "days after picture day"', () => {
    const far = new Date(Date.now() + 10 * 86400000).toISOString().slice(0, 10);
    const text = render({ promoHeadline: '20% OFF', promoSubhead: 'ignored', expiresOn: far });

    expect(text).toContain('Offer good through');
    expect(text).not.toContain('ignored');
  });

  it('keeps the offer visible in the urgent phase, not just the urgency', () => {
    // The loudest banner must still say what it gives.
    const text = render({ promoHeadline: '20% OFF', expiresOn: localDateIn(2) });

    expect(text).toContain('20% OFF');
    expect(text).toContain('DAYS LEFT');
  });

  // The urgent prefix shipped as "LAST DAY IN 2 — ": missing its noun, so the em
  // dash read as a trailing hyphen on a cut-off sentence. It also counted off the
  // LAST day while the reminder phase counted days remaining, so the two disagreed.
  // Both phases now use one phrasing and one count.
  describe('the countdown prefix', () => {
    it.each([
      [1, '2 DAYS LEFT'],
      [2, '3 DAYS LEFT'],
      [3, '4 DAYS LEFT'],
      [4, '5 DAYS LEFT'],
      [6, '7 DAYS LEFT'],
    ])('reads "%s" when the offer ends in +%i days', (offset, expected) => {
      const text = render({ promoHeadline: '20% OFF', expiresOn: localDateIn(offset) });

      expect(text).toContain(expected);
    });

    // The bug that made this worth pinning: urgent counted `days - 1` while
    // reminder counted `days`, so crossing the boundary looked like a skipped day.
    it('does not skip a day where urgent meets reminder', () => {
      const counts = [1, 2, 3, 4, 5, 6].map((offset) => {
        const text = render({ promoHeadline: '20% OFF', expiresOn: localDateIn(offset) });
        return Number(text.match(/(\d+) DAYS LEFT/)[1]);
      });

      expect(counts).toEqual([2, 3, 4, 5, 6, 7]);
    });

    // #5192 reported "LAST DAY IN 1" on the shop pages. `days` counts today, so
    // an offer ending tomorrow is 2 DAYS LEFT and an offer ending today is the
    // ticking ENDS IN clock -- no phase can produce a bare "IN 1".
    it('reads a countdown clock, not "LAST DAY IN 1", on the final day', () => {
      const text = render({ promoHeadline: '20% OFF', expiresOn: localDateIn(0) });

      // Countdown itself is a hook component (ticks every second), so the
      // direct-call render shows its fallback; the final phase is identified by
      // its subhead and the clock's label is pinned in offerDeadline.test.js.
      expect(text).toContain('Last chance for 20% off');
      expect(text).not.toMatch(/LAST DAY|\d+ DAYS? LEFT/);
    });

    it('never says LAST DAY IN at any day count', () => {
      [0, 1, 2, 3, 4, 6, 10].forEach((offset) => {
        const text = render({ promoHeadline: '20% OFF', expiresOn: localDateIn(offset) });

        expect(text).not.toMatch(/LAST DAY|\bIN 1\b/);
      });
    });

    it('never emits a bare number with no unit before the dash', () => {
      [1, 2, 3, 4, 6].forEach((offset) => {
        const text = render({ promoHeadline: '20% OFF', expiresOn: localDateIn(offset) });

        expect(text).not.toMatch(/\d+ —/);
      });
    });
  });

  it('renders nothing when there is no offer at all', () => {
    expect(PromoBanner({ offer: null, className: '' })).toBeNull();
    expect(PromoBanner({ offer: undefined, className: '' })).toBeNull();
  });
});
